import { get, put, post, upload } from './SimpleClient';
import moment from 'moment';
import {
  getBrandingIds,
  getKey,
  setKey,
  removeJobFlags,
  removeSubmittedChecklists,
  removeSubmittedEstimates,
  removeSubmittedNotes,
  updateStoredObjectKey,
  addToStoredArray,
  deleteKey,
  getStoredObjectKey,
  getJob,
  getBooking,
  storeSubmittedChecklists,
} from './Storage';
import { getUpdates } from './Updates';
import {userIsCleaner} from "../dataStructures/user/predicates";
import {UserFriendlyError} from "./UserFriendlyError"

export const storePlans = async (startDate, numberOfDays = 1, page = 1, limit = 999) => {
  let user = getUser();

  if (!user.id) {
    return false;
  }

  let jobIds = [];
  let propertyIds = [];

  const updates = await getUpdates();
  
  let dates = [];

  let date = moment(startDate);
  for (let i = 0; i < numberOfDays; i++) {
    dates.push(date.format('YYYY-MM-DD'));
    date.add(1, 'days');
  }

  const plans = await get(
    `dayplan?page=${page}&limit=${limit}&filter=supplierid=${user.id}:plandate=${dates.join('|')}`
  );

  if (!plans) {
    return false;
  }

  for (const result of plans.elements) {
    const plan = result.dayplan;
    await updateStoredObjectKey('plans', result.plandate, plan);

    const parsedPlan = JSON.parse(plan);
    if (parsedPlan.jobs) {
      // Ensure jobs exist before accessing
      Object.values(parsedPlan.jobs).forEach((job) => {
        jobIds.push(job.id);
        if (!propertyIds.includes(job.property.id)) {
          propertyIds.push(job.property.id);
        }
      });
    }
  }

  if (jobIds.length > 0) {
    const workorders = await get(`workorder?filter=id=${jobIds.join('|')}&view=pms&limit=99`);

    for (let workorder of workorders.elements) {
      await updateStoredObjectKey('jobs', workorder.id, workorder);

      if (updates.length === 0) {
        await removeJobFlags(workorder.id);
        await removeSubmittedNotes(workorder.id);
        await removeSubmittedEstimates(workorder.id);
        await removeSubmittedChecklists(workorder.id);
      }
    }
  }

  await requestPropertyListById(propertyIds);

  return true;
}

export const requestPropertyListById = async (propertyIds = []) => {
  if (propertyIds.length > 0) {
    const properties = await get(`property?filter=id=${propertyIds.join('|')}&view=pms&limit=99`);
    if (properties) {
      for (let property of properties.elements) {
        await updateStoredObjectKey('properties', property.id, property);
      }
    }
  }
}

export const storeEstimateJobs = async () => {
  const user = getUser();
  const updates = await getUpdates();

  const jobs = await get(
    `workorder?filter=type=Instance:assignedtoactorid=${user.id}:substatus=field_worker_to_provide_estimates&limit=999`
  );

  if (jobs) {
    await setKey('estimateJobs', JSON.stringify(jobs.elements));

    const jobIds = jobs.elements.map(e => {
      return e.id
    });

    if (jobIds.length > 0) {
      const workorders = await get(`workorder?filter=id=${jobIds.join('|')}`);
      for (let workorder of workorders.elements) {
        await updateStoredObjectKey('jobs', workorder.id, workorder);
  
        if (updates.length === 0) {
          await removeJobFlags(workorder.id);
          await removeSubmittedNotes(workorder.id);
          await removeSubmittedEstimates(workorder.id);
          await removeSubmittedChecklists(workorder.id);
        }      
      }
    }
  }

  return true;
}

export const storeSelfEmployedProperties = async (userId) => {
  const jobIds = await get(
      `workorder?page=1&filter=supplierid=${userId}:status=Invoiced|Invoice Rejected|Completed|Approved|Invoice Approved:type=Instance&fields=id&limit=1`
  );
  const totalPages = Math.ceil(jobIds.total / 500);

  const requests = [];

  for (let page = 1; page <= totalPages; page++) {
    requests.push(get(`workorder?page=${page}&filter=supplierid=${userId}:status=Invoiced|Invoice Rejected|Completed|Approved|Invoice Approved:type=Instance&fields=property:status&limit=500`));
  }

  const allPages = await Promise.all(requests);
  const jobs = allPages.flatMap(page => page.elements);

  const propertyData = [
    ...new Map(
        jobs.map(job => [job.property.id, job.property])
    ).values()
  ];

  if (propertyData) {
    propertyData.sort((a, b) => a.name.localeCompare(b.name));
    await setKey('allproperties', JSON.stringify(propertyData));
  }

  return propertyData;
}
export const storeSelfEmployedPropertiesNano = async (userId) => {

  const workorders = await get(
      `workorder?page=1&filter=supplierid=${userId}:status=Invoiced|Invoice Rejected|Completed|Approved|Invoice Approved:type=Instance&fields=id:propertynano&limit=999999`
  );

  let propertyData = [];
  for (let workorder of workorders.elements) {
    if (!propertyData.some((property) => property.id ===  workorder.propertynano.id)) {
      propertyData.push( workorder.propertynano);
    }
  }

  await setKey('allpropertiesnano', JSON.stringify(propertyData));

  return propertyData;
}

export const storePropertiesFromBookings = async (userId) => {
  let propertyData = [];

  let filterQueries = [
    `confirmedbooking=1:cancelledbooking=0:transferredbooking=0:actorid=${userId}`,
    `type=Owner:cancelledbooking=0:actorid=${userId}`,
    `type=agency:transferredbooking=0:cancelledbooking=0:actorid=${userId}`,
  ];

  const today = new Date();
  const toDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - 365
  ).toISOString().split('T')[0];

  let url = `/booking?page=1&limit=9999999&orderBy=fromdate&fields=property`;
  for (let query of filterQueries) {
    url = url + '&filter[]='+ query + `:fromdate=>${toDate}`;
  }

  let bookings =  await get(url)

  if (!bookings.elements) {
    return propertyData;
  }

  for (const booking of bookings.elements){
    if (!propertyData.some((property) => property.id === booking.property.id)) {
      propertyData.push(booking.property);
    }
  }

  if (propertyData) {
    propertyData.sort((a, b) => a.name.localeCompare(b.name));
    await setKey('allproperties', JSON.stringify(propertyData));
  }

  return propertyData;
}

export const generatePropertyQAndAReport = async (propertyId) => {

  const reportId = await getReportIdByFileName('PropertyQuestionAnswer.rptdesign');

  let report = await post(`report/${reportId}/run`, {
    PropertyId: propertyId,
    _format: 'pdf',
  });

  return report;
}

export const generateArrivalsReport = async () => {
  const user = getUser();
  const date = new Date();
  const fromDate = date.toISOString().split('T')[0];
  date.setMonth(date.getMonth() + 1);
  const toDate = date.toISOString().split('T')[0];

  const reportUrl =  await post(`supplier/${user.id}/arrivalsreport`, {
    fromdate: fromDate,
    todate: toDate,
    shownights: true,
    donotgroup: true,
    arrivalsordepartures: 'both',
    _format: 'pdf'
  }, true);

  return await get(reportUrl);
}


export const getReportIdByFileName = async (fileName) => {

  const cachedId = await getStoredObjectKey('reportIds', fileName, null);

  if (null !== cachedId) {
    return cachedId;
  }

  let reportList = await get(`report?filter=filename=${fileName}`);

  const reportId = reportList.elements[0].id;

  if (reportId) {
    await updateStoredObjectKey('reportIds', fileName, reportId);
  }

  return reportId;
}

export const getSettingBySettingName = async (settingName) => {
  const settings = await get(`setting`);
  const filteredSettings = settings.filter((setting => setting.name.toLowerCase() === settingName.toLowerCase()));

  return filteredSettings.length ? filteredSettings[0] : null;
}
export const getDefaultSupplierId = async () => {

  const cachedId = await getKey('defaultSupplierId', null);

  if (null !== cachedId && cachedId) {
    return cachedId;
  }

  const setting = await getSettingBySettingName('PMS holding supplier');

  if (setting.defaultvalue) {
    await setKey('defaultSupplierId', setting.defaultvalue);
  }

  return setting.defaultvalue;
}


export const storeJobsById = async (jobId) => {
  const job = await get(`workorder/${jobId}`);
  await updateStoredObjectKey('jobs', jobId, job);

  return job;
}

export const storeSelfEmployedJobs = async (page = 0, limit = 999, filters = {}, otherFields = "") => {
  const jobs = await fetchJobs(page, limit, filters, otherFields);

  if (jobs) {
    await deleteKey('selfEmployedJobs');
    await deleteKey('selfEmployedJobsStats');

    for (let workorder of jobs.elements) {
      await addToStoredArray('selfEmployedJobs', workorder);
    }

    await storeSelfEmployedJobsStats(jobs, filters);
  }

  return {
    jobs: jobs.elements ?? [],
    total: jobs.total,
    page: jobs.page,
    limit: jobs.limit,
  };
};

export const storeBookings = async (page = 0, limit = 999, filters = {}, otherFields = "") => {
  const items = await fetchBookings(page, limit, filters, otherFields);

  if (items) {
    await deleteKey('bookings');
    await deleteKey('bookingsStats');

    for (let item of items.elements) {
      await updateStoredObjectKey('bookings', item.fromdate + item.id, item);
    }

    await storeBookingsStats(items, filters);
  }

  return {
    jobs: items.elements ?? [],
    total: items.total,
    page: items.page,
    limit: items.limit,
  };
};

export const storeSelfEmployedInvoices = async (page = 0, limit = 999, filters = {}, otherFields = "") => {
  const jobs = await fetchJobs(page, limit, filters, otherFields, 'instancepreferredstartdatetime_desc');

  if (jobs) {
    await deleteKey('selfEmployedInvoices');

    for (let workorder of jobs.elements) {
      await addToStoredArray('selfEmployedInvoices', workorder);
    }

    await storeSelfEmployedInvoicesStats(jobs, filters);
  }

  return {
    jobs: jobs.elements ?? [],
    total: jobs.total,
    page: jobs.page,
    limit: jobs.limit,
  };
};

export const fetchJobs = async (page = 0, limit = 999, filters = {}, otherFields = "", order = 'instancepreferredstartdatetime') => {
  const user = getUser();
  let filterQuery = [`supplierid=${user.id}:type=Instance`];

  // Build filter conditions
  if (filters.status) {
    filterQuery.push(`status=${filters.status}`);
  }
  if (filters.propertyId) {
    filterQuery.push(`propertyid=${filters.propertyId}`);
  }
  if (filters.requiredBy) {
    // filters.requiredBy should include operator, e.g., "=2026-01-05" or "<2026-01-05"
    filterQuery.push(`requiredbydate${filters.requiredBy}`);
  }
  if (filters.invoiceapproveddatetime) {
    // filters.requiredBy should include operator, e.g., "=2026-01-05" or "<2026-01-05"
    filterQuery.push(`invoiceapproveddatetime${filters.invoiceapproveddatetime}`);
  }

  // Combine into single filter string, separated by colon
  const filtersString = filterQuery.join(':');

  const pageQuery = page > 0 ? `&page=${page}` : '';

  let url = "";
    //if we have a required by date we also need to run filterquery by preferred start date
  if (filters.requiredBy) {
    const filtersString2 = filtersString.replaceAll('requiredbydate', 'preferredstartdatetime');
    url = `workorder?filter[]=${filtersString}&filter[]=${filtersString2}${pageQuery}&limit=${limit}&orderBy=${order}&fields=id:shortdescription:fulldescription:property:substatus:status:expenses:booking:preferredstartdatetime:requiredbydate`;
  } else {
      url = `workorder?filter=${filtersString}${pageQuery}&limit=${limit}&orderBy=${order}&fields=id:shortdescription:fulldescription:property:substatus:status:expenses:booking:preferredstartdatetime:requiredbydate`;
  }


  if (otherFields.length) {
    url += `:${otherFields}`;
  }

  return await get(url);
};

export const fetchBookings = async (page = 0, limit = 999, filters = {}, otherFields = "", order = 'instancepreferredstartdatetime') => {
  const user = getUser();
  let propertyFilter = '';
  if (filters.propertyId) {
    propertyFilter = `:propertyid=${filters.propertyId}`;
  }
  let filterQueries = [
    `confirmedbooking=1:cancelledbooking=0:transferredbooking=0:actorid=${user.id}${propertyFilter}`,
    `type=Owner:cancelledbooking=0:actorid=${user.id}${propertyFilter}`,
    `type=agency:transferredbooking=0:cancelledbooking=0:actorid=${user.id}${propertyFilter}`,
  ];

  const today = moment().format('YYYY-MM-DD');

  let fromdatequery = `:fromdate=>${today}`;
  if(filters.status >=  90) {
    const fromDate = moment().subtract(filters.status, 'days').format('YYYY-MM-DD');
    fromdatequery = `:fromdate=${fromDate}/${today}`;
  }

  let url = `/booking?page=${page}&limit=${limit}&orderBy=fromdate_asc`;
  for (let query of filterQueries) {
    url = url + '&filter[]='+ query + fromdatequery;
  }

  return await get(url);
};

export const requestBooking = async (id) => {
  const url = `booking/${id}`

  const booking = await get(url);
  await updateStoredObjectKey('bookings', booking.id, booking);
  return booking;
}

export const storeChecklistForJob = async (id) => {
  const url = `workorder/${id}/pmschecklist`

  const checklists = await get(url);

  let job = await getJob(id);
  if (!job.id) {
    // Account for data that's not been preloaded.
    job = await storeJobsById(id);
  }

  await updateStoredObjectKey('jobs', id, {
    ...job,
    checklists,
  });

  return checklists;
}

export const requestBookingRooms = async (id) => {
  const url = `booking/${id}/room`

  const rooms = await get(url);
  await updateStoredObjectKey('bookingRooms', id, rooms);
  return rooms;
}

export const requestPropertyRooms = async (id) => {
  const url = `property/${id}/room`

  const rooms = await get(url);
  await updateStoredObjectKey('propertyRooms', id, rooms);
  return rooms;
}

export const requestSamePropertyBookingsCount = async (bookingId, customerId, propertyId) => {
  const hash = [bookingId, customerId, propertyId].join('|');
  if (null === customerId) {
    await updateStoredObjectKey('bookingCount', hash, 0);

    return 0;
  }

  const url = `booking?page=1&limit=10&filter=id=!${bookingId}:customer_id=${customerId}:propertyid=${propertyId}:confirmedbooking=1:cancelledbooking=0:transferredbooking=0&fields=id`;

  const bookings = await get(url);

  await updateStoredObjectKey('bookingCount', hash, bookings.count);

  return bookings.count ?? 0;
}

export const requestBookingNotes = async (id) => {
  const url = `booking/${id}/note`

  const notes = await get(url);

  await updateStoredObjectKey('bookingNotes', id, notes.elements);
  return notes.elements ?? [];
}

export const requestJobData = async (id) => {

  if (null === id) {
    return ;
  }

  await storeJobsById(id);
  const existingJobData = await getJob(id);

  if(!existingJobData) {
    return null;
  }

  await storeChecklistForJob(id);
  const propertyId = existingJobData.property.substr(existingJobData.property.lastIndexOf("/") + 1);
  await requestPropertyListById([propertyId]);

  await requestPropertyRooms(propertyId);
  if (existingJobData?.booking?.id) {
    await requestBooking(existingJobData?.booking?.id);
    await requestBookingRooms(existingJobData?.booking?.id);
    await requestBookingNotes(existingJobData?.booking?.id);

    const bookingData = await getBooking(existingJobData?.booking?.id);
    const customers = bookingData?.customers ?? [];
    await requestSamePropertyBookingsCount(bookingData.id, customers[0]?.id ?? null, bookingData.property.id);
  }

  return true;
}

export const storeSelfEmployedJobsStats = async (jobs = {}, filters = {}) => {
  await updateStoredObjectKey('selfEmployedJobsStats', 'total', jobs.total);
  await updateStoredObjectKey('selfEmployedJobsStats', 'currentPage', jobs.page);
  await updateStoredObjectKey('selfEmployedJobsStats', 'pageLimit', jobs.limit);
  await updateStoredObjectKey('selfEmployedJobsStats', 'filters', filters);

  return true;
}
export const storeBookingsStats = async (items = {}, filters = {}) => {
  await updateStoredObjectKey('bookingsStats', 'total', items.total);
  await updateStoredObjectKey('bookingsStats', 'currentPage', items.page);
  await updateStoredObjectKey('bookingsStats', 'pageLimit', items.limit);
  await updateStoredObjectKey('bookingsStats', 'filters', filters);

  return true;
}

export const storeSelfEmployedInvoicesStats = async (jobs = {}, filters = {}) => {
  await updateStoredObjectKey('selfEmployedInvoicesStats', 'total', jobs.total);
  await updateStoredObjectKey('selfEmployedInvoicesStats', 'currentPage', jobs.page);
  await updateStoredObjectKey('selfEmployedInvoicesStats', 'pageLimit', jobs.limit);
  await updateStoredObjectKey('selfEmployedInvoicesStats', 'filters', filters);

  return true;
}

export const storeSelfEmployedJobsCount = async (filters = {}) => {
  const user = getUser();
  const statusList = ['Approved', 'Completed', 'Invoiced', 'Invoice Rejected', 'Invoice Approved'];
  let filterQuery = [`supplierid=${user.id}:type=Instance`];

  if (filters.propertyId) {
    filterQuery.push(`propertyid=${filters.propertyId}`);
  }
  if (filters.requiredBy) {
    filterQuery.push(`requiredbydate${filters.requiredBy}`);
  }

  // Combine into single filter string, separated by colon
  const filtersString = filterQuery.join(':');

  let url;
  await deleteKey('selfEmployedJobsCount');
  for (const substatus of statusList) {
    if (filters.requiredBy) {
      const filtersString2 = filtersString.replaceAll('requiredbydate', 'preferredstartdatetime');
      url = `workorder?&filter[]=${filtersString}:status=${substatus}&filter[]=${filtersString2}:status=${substatus}`;
    } else {
      url = `workorder?filter=${filtersString}:status=${substatus}`;
    }
    const jobs = await get(
        `${url}&limit=1&page=1&fields=id`
    );
    await updateStoredObjectKey('selfEmployedJobsCount', substatus, jobs.total);
  }

  return true;
}

export const storeBookingsCount = async (filters = {}) => {
  const user = getUser();
  const periodList = [30, 90, 180, 365];
  const today = new Date();
  let propertyFilter = '';
  if (filters.propertyId) {
    propertyFilter = `:propertyid=${filters.propertyId}`;
  }
  let filterQueries = [
      `confirmedbooking=1:cancelledbooking=0:transferredbooking=0:actorid=${user.id}${propertyFilter}`,
      `type=Owner:cancelledbooking=0:actorid=${user.id}${propertyFilter}`,
      `type=agency:transferredbooking=0:cancelledbooking=0:actorid=${user.id}${propertyFilter}`,
  ];

  await deleteKey('bookingsCount');
  for (const period of periodList) {
    let url = '/booking?page=1&limit=1&fields=id';
    const toDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - period
    ).toISOString().split('T')[0];

    for (let query of filterQueries) {
      url = url + '&filter[]='+ query + `:fromdate=>${toDate}`;
    }
    const items = await get(
        `${url}`
    );

    await updateStoredObjectKey('bookingsCount', period, items.total);
  }

  return true;
}

export const storeSubStatuses = async () => {
  const result = await get('workordersubstatus');
  if (result) {
    return await setKey('workordersubstatus', JSON.stringify(result));
  }
  
  return result;
}

export const storeSettings = async () => {
  const result = await get('setting');
  if (result) {
    return await setKey('settings', JSON.stringify(result));
  }
  
  return result;
}

export const storeSelfEmployedUserProperties = async () => {
  const user = getUser();
  const properties = await get(
    `property?filter=statusid=1|3:activesupplierid=${user.id}&fields=id:name:tabspropref:address:housekeeping&limit=9999&orderBy=name`
  );

  if (properties) {
    await setKey('associatedproperties', JSON.stringify(properties.elements));
  }

  return properties;
}

export const storeProperties = async () => {
  const brandingIds = await getBrandingIds();

  if (brandingIds.length === 0) { return []; }

  const properties = await get(
      `property?filter=statusid=1|3:brandingid=${brandingIds.join('|')}&fields=id:name:tabspropref:address&limit=9999&orderBy=name`
  );

  if (properties) {
    await setKey('associatedproperties', JSON.stringify(properties.elements));
  }

  return properties;
}

export const fetchUserAssociatedProperties = async () => {
  let propertyData = [];
  const selfEmployedUserProperties = await storeSelfEmployedUserProperties();

  if (selfEmployedUserProperties?.elements?.length) {
    for (const item of selfEmployedUserProperties.elements){
      if (!propertyData.some((property) => property.id === item.id)) {
        propertyData.push(item);
      }
    }
  }

  return propertyData;
}


export const addNoteToJob = async (jobId, noteText, photos = [], documents = []) => {
  const user = getUser();

  var dPromises = [];

  photos.forEach(photo => {
    dPromises.push(
      upload('image', {
        data: {
          uri: photo,
          name: 'JPEG_' + moment().format('YYYYMMDD_HHmmss') + '.jpg' // photo.substr(photo.lastIndexOf("/") + 1)
        }
      })
    );
  });

  documents.forEach(document => {
    dPromises.push(
      upload('document', {
        data: {
          uri: document.base64,
          name: document.name,
        }
      })
    );
  });

  const dPromiseResults = await Promise.all(dPromises);

  let wodPromises = [];

  dPromiseResults.forEach(result => {
    if(result) {
      wodPromises.push(
        post(
          `workorder/${jobId}/document`,
          { documentid: result.substr(result.lastIndexOf("/") + 1) }
        )
      );
        }
  });

  let workOrderDocuments = await Promise.all(wodPromises);

  if (noteText) {
    let nt = noteText;

    let workOrderDocumentIds = workOrderDocuments.map(wod => {
      return wod.substr(wod.lastIndexOf("/") + 1)
    });

    if (workOrderDocumentIds.length > 0) {
      nt += `\n\n[documents:${workOrderDocumentIds.join(',')}]`;
    }

    let note = await post('note', {
      notetype: 'Normal',
      subject: 'field worker note',
      createdbyid: user.id,
      notetext_createdbyactorid: user.id,
      notetext_notetext: nt,
    });

    await post(
      `note/${note.substr(note.lastIndexOf("/") + 1)}/actor`, 
      {
        actorid: user.id,
        notifychanges: false,
      }
    );

    await post('workordernote', {
      workorderid: jobId,
      noteid: note.substr(note.lastIndexOf("/") + 1),
    });

    return true;
  }
}

export const addLogNote = async data => {

  const user = getUser();

  let note = await post('note', {
    notetype: 'Normal',
    subject: 'PMS app log',
    createdbyid: user.id,
    notetext_createdbyactorid: user.id,
    notetext_notetext: JSON.stringify(data),
  });

  await post(
    `note/${note.substr(note.lastIndexOf("/") + 1)}/actor`, 
    {
      actorid: 1,
      notifychanges: false,
    }
  );

  return true;
}

export const jobStarted = async (jobId, fromdatetime = null) => {
  const result = await put(`workorder/${jobId}`, {
    starteddate: fromdatetime ? moment(fromdatetime).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
    fromdatetime: fromdatetime || moment().format('YYYY-MM-DD HH:mm:ss'),
  });
  
  if (result) {
    return await updateJobSubStatus(jobId, 'started', null, null, fromdatetime);
  } else {
    return false; 
  }
}

export const jobPaused = async (jobId, fromdatetime = null) => {
  return await updateJobSubStatus(jobId, 'started_paused', null, null, fromdatetime);
}

export const jobCompleted = async (jobId, fromdatetime = null) => {
  const user = getUser();
  let data = {
    completeddate: fromdatetime ? moment(fromdatetime).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
  };

  if (!userIsCleaner(user)) {
    data.fromdatetime = fromdatetime || moment().format('YYYY-MM-DD HH:mm:ss');
  }

  const result = await put(`workorder/${jobId}`, data);

  if (userIsCleaner(user)) {
    return true;
  }

  if (result) {
    return await updateJobSubStatus(jobId, 'completed', null, null, fromdatetime);
  } else {
    return false;
  }
}

export const jobCannotDo = async (jobId, noteText, noteSubject, fromdatetime = null) => {
  return await updateJobSubStatus(jobId, 'could_not_complete', noteText, noteSubject, fromdatetime);
}

export const jobCannotStart = async (jobId, noteText, noteSubject, fromdatetime = null) => {
  return await updateJobSubStatus(jobId, 'cannot_start_job', noteText, noteSubject, fromdatetime);
}

export const jobEstimatesProvided = async jobId => {
  return await updateJobSubStatus(jobId, 'field_worker_provided_estimates');
}

export const submitCosts = async (jobId, invoicedate = null) => {
  const result = await post(`workorder/${jobId}/invoice`, {
    supplierinvoice_invoicedate: invoicedate ? moment(invoicedate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
  }, true);

  if (result?.errorDescription) {
    throw new UserFriendlyError(result?.errorCode == 474218 ?  `Cannot invoice work order #${jobId} with invalid expenses`: "Could not generate report");
  }

  return result;
}

export const addNewExpense = async (jobId, obj) => {
  return await post(`workorder/${jobId}/expense`, obj);
}

export const updateExpense = async (jobId, expenseId, obj) => {
  return await put(`workorder/${jobId}/expense/${expenseId}`, obj);
}

export const updateChecklist = async (jobId, workOrderPmsChecklistId, obj) => {
  return await put(`workorder/${jobId}/pmschecklist/${workOrderPmsChecklistId}`, obj);
}

export const fetchSubmittedChecklists = async (jobId) => {
  const submittedChecklists = await get(`workorder/${ jobId }/pmschecklist`);
  await storeSubmittedChecklists(
      jobId,
      submittedChecklists,
  );

  return submittedChecklists;
}

export const addSupplierLocation = async (obj) => {
  const user = getUser();

  return await post(
    `actor/${user.id}/location`,
    obj
  );
}

export const addActorDevice = async deviceId => {
  const user = getUser();

  return await post(
    `actor/${user.id}/device`,
    {
      deviceId,
      registereddatetime: moment().format('YYYY-MM-DD HH:mm:ss')
    }
  );
}

export const createJob = async obj => {
  const location = await post(
    'workorder',
    obj
  );
  
 const jobId = location && typeof location === 'string' && location.lastIndexOf('/') !== -1
  ? location.substr(location.lastIndexOf('/') + 1)
  : null;

  if (obj.preferredstartdatetime) {
    await updateJobSubStatus(jobId, 'scheduled');

    await put(
      `workorder/${jobId}`,
      { starteddate: moment(obj.preferredstartdatetime).format('YYYY-MM-DD') }
    );

    await updateJobSubStatus(jobId, 'started');

    await storePlans(moment().format('YYYY-MM-DD'), 1);
  }

  return true;
}

export const getFileData = async id => {
  const result = await get('file/' + id);
  if (result) {
    return result;
  }
  
  return result;
}

const updateJobSubStatus = async (jobId, subStatusRef, noteText, noteSubject, fromdatetime) => {

  const user = getUser();
  const job = await get(`workorder/${jobId}`);

  let noteId = undefined;
  if (noteText) {
    let note = await post('note', {
      notetype: 'Normal',
      subject: noteSubject || 'Note from field worker app',
      createdbyid: user.id,
      notetext_createdbyactorid: user.id,
      notetext_notetext: noteText,
    });

    await post(
      `note/${note.substr(note.lastIndexOf("/") + 1)}/actor`, 
      {
        actorid: user.id,
        notifychanges: false,
      }
    );

    noteId = note.substr(note.lastIndexOf("/") + 1);

    await post('workordernote', {
      workorderid: jobId,
      noteid: noteId,
    });
  }

  // get the current WorkOrderStatusHistory
  let WorkOrderStatusHistory = undefined;
  for (const status of job.statushistory.reverse()) {
    if (status.status === job.status) {
      WorkOrderStatusHistory = status;
      break;
    }
  }

  // get the WorkOrderSubStatus we want
  let workOrderSubStatus = undefined;
  const workOrderSubStatuses = await getKey('workordersubstatus');

  if (workOrderSubStatuses) {
    workOrderSubStatuses.forEach(woss => {
      if (woss.substatusreference === subStatusRef) {
        workOrderSubStatus = woss;
      }
    });

    let obj = {
      workordersubstatusid: workOrderSubStatus.id,
      fromdatetime: fromdatetime || moment().format('YYYY-MM-DD HH:mm:ss'),
      setbyactorid: user.id,
    }

    if (noteId) {
      obj.noteid = noteId;
    }

    return await post(
        `workorder/${jobId}/statushistory/${WorkOrderStatusHistory.id}/substatus`,
        obj
    );
  }
}

export const getUser = () => {
  return JSON.parse(localStorage.apiuser || "{}");
}
